import React, { useEffect, useRef, useContext, useState } from "react";
import {
	Button,
	Table,
	Form,
	Input,
	Upload,
	message,
} from "antd";
import { useModel } from "../../../models";
import PurchaseItemSelectionModel from "./PurchaseItemSelectionModel";
import { v4 as uuidV4 } from "uuid";
import {
	DICT_KEY,
	getDictNameByCode,
	formatNum,
	ENV
} from "../../../utils/constants";
import PurchaseRoService from "./PurchaseRoService";
import { YhAuth } from "../../../components/Auth";
import { ResourceAuthEnum } from "../../../utils/resourcesAuth";

const EditableContext = React.createContext();

const EditableRow = ({ index, ...props }) => {
	const [form] = Form.useForm();
	return (
		<Form form={form} component={false}>
			<EditableContext.Provider value={form}>
				<tr {...props} />
			</EditableContext.Provider>
		</Form>
	);
};
const EditableCell = ({
	title,
	editable,
	children,
	dataIndex,
	record,
	handleSave,
	editList,
	column,
	selectList,
	...restProps
}) => {
	const [editing, setEditing] = useState(false);
	const inputRef = useRef();
	const form = useContext(EditableContext);
	useEffect(() => {
		if (editing) {
			inputRef.current.focus();
		}
	}, [editing]);

	const onKeyDown = async (event, isDefault = false) => {
		let node = event.target;
		let trParentNode = node.closest("tr");
		let checkRow = 1; //这里查看表格是否为多选状态
		let findIndex = editList.findIndex((item) => item.id == record.id);
		let findInd = column.findIndex((item) => item.dataIndex == dataIndex);
		if (event.keyCode === 40 && findIndex !== editList.length - 1) {
			trParentNode.nextSibling.children[findInd + checkRow].children[0].click();
		}
		if (event.keyCode === 38 && findIndex !== 0) {
			trParentNode.previousSibling.children[
				findInd + checkRow
			].children[0].click();
		}
		if (event.keyCode === 9) {
			event.stopPropagation();
			event.preventDefault();
			let reducerArr = column.reduce((cur, pre, index) => {
				if (pre.editable) {
					cur.push(index + checkRow);
				}
				return cur;
			}, []);
			let subscript = reducerArr.indexOf(findInd + checkRow);
			if (subscript === reducerArr.length - 1) {
				trParentNode.children[reducerArr[0]].children[0].click();
			} else {
				trParentNode.children[reducerArr[subscript + 1]].children[0].click();
			}
		}
		if (event.keyCode === 13) {
			event.stopPropagation();
			const values = await form.validateFields();
		}
	};

	const toggleEdit = () => {
		setEditing(!editing);
		form.setFieldsValue({
			[dataIndex]: record[dataIndex],
		});
	};

	const save = async (e) => {
		try {
			const values = await form.validateFields();
			toggleEdit();
			handleSave({ ...record, ...values });
		} catch (errInfo) {
			console.log("Save failed:", errInfo);
		}
	};
	const onFocus = () => {
		inputRef.current.select();
	};
	let childNode = children;

	const isNumber = (rule, value, callback) => {
		if (!Number.isFinite(value - 0)) {
			return Promise.reject("请输入数字");
		}
		return Promise.resolve();
	};
	const inputRules = (dataIndex) => {
		if (dataIndex === "returnNum") {
			return [
				{ required: true, message: `不能为空.` },
				{ validator: isNumber, trigger: "blur" },
				() => ({
					validator: (_, value) => {
						let { purchaseNum, retiredNum } = record;
						purchaseNum = purchaseNum ? parseInt(purchaseNum) : 0;
						retiredNum = retiredNum ? parseInt(retiredNum) : 0;
						let returnNum = value ? parseInt(value) : 0;
						if (returnNum > purchaseNum - retiredNum) {
							return Promise.reject("退货数量<=采购数量-已退货数量");
						}
						return Promise.resolve();
					},
				}),
			];
		} else if (dataIndex === "returnDesc") {
			return [{ required: true, message: `不能为空.` }];
		} else if (dataIndex === "returnPrice") {
			return [
				{ required: true, message: `不能为空.` },
				{ validator: isNumber, trigger: "blur" },
			];
		} else if (dataIndex === "discount") {
			return [
				{ required: true, message: `不能为空.` },
				{ validator: isNumber, trigger: "blur" },
				{
					pattern: /^(([0-9]\d?)|100)$/g,
					message: "折扣必须是大于等于0且小于等于100的整数",
				},
			];
		}
	};
	const inputTypes = (dataIndex, record) => {
		if (dataIndex === "returnNum") {
			return (
				<Input
					ref={inputRef}
					onPressEnter={save}
					onFocus={onFocus}
					onBlur={save}
					style={{ width: "100%" }}
					onKeyDown={onKeyDown}
				/>
			);
		} else if (dataIndex === "returnPrice") {
			return (
				<Input
					ref={inputRef}
					onPressEnter={save}
					onBlur={save}
					style={{ width: "100%" }}
					onKeyDown={onKeyDown}
				/>
			);
		} else if (dataIndex === "discount") {
			return (
				<Input
					ref={inputRef}
					onPressEnter={save}
					onBlur={save}
					style={{ width: "100%" }}
					formatter={(value) => `${value}%`}
					parser={(value) => value.replace("%", "")}
					onKeyDown={onKeyDown}
				/>
			);
		} else if (dataIndex === "returnDesc") {
			return (
				<Input.TextArea
					ref={inputRef}
					onBlur={save}
					style={{ width: "100%" }}
					onKeyDown={onKeyDown}
				/>
			);
		}
	};

	if (editable) {
		childNode = editing ? (
			<Form.Item
				style={{
					margin: 0,
				}}
				name={dataIndex}
				rules={inputRules(dataIndex)}
			>
				{inputTypes(dataIndex, record)}
			</Form.Item>
		) : (
			<div
				className="editable-cell-value-wrap"
				style={{
					paddingRight: 24,
				}}
				onClick={toggleEdit}
			>
				{children}
			</div>
		);
	}

	return <td {...restProps}>{childNode}</td>;
};

export default function ({ getForm }) {
	const [
		{
			documentMaterialList,
			selectedDocument,
			selectedDocumentMaterialRowKeys,
			detailStatistics,
		},
		{
			setDocumentMaterialList,
			removeDocumentMaterial,
			downloadDetailTpl,
			setSelectedDocumentMaterialRowKeys,
			setAddMaterialModelVisible,
			setDetailStatistics,
		},
	] = useModel("purchaseRo");
	const { orderStatus = "027001" } = selectedDocument;
	//财务确认
	const editable01 = orderStatus === "027001" || orderStatus === "027002";
	//库房确认
	const editable02 = orderStatus === "027001";
	useEffect(() => {
		let roTotalNum = 0,
			roTotalAmount = 0.0,
			roReceivableAmount = 0.0;
		documentMaterialList.forEach((el) => {
			const { returnNum, returnPrice, receivableAmount } = el;
			roTotalNum = roTotalNum + (returnNum ? parseInt(returnNum) : 0);
			roReceivableAmount =
				roReceivableAmount +
				(receivableAmount ? parseFloat(receivableAmount) : 0.0);
			roTotalAmount =
				roTotalAmount +
				(!!returnNum && !!returnPrice
					? parseInt(returnNum) * parseFloat(returnPrice)
					: 0.0);
		});
		setDetailStatistics({
			roTotalNum,
			roReceivableAmount: roReceivableAmount.toFixed(2),
			roTotalAmount: roTotalAmount.toFixed(2),
		});
	}, [documentMaterialList]);

	const onAdd = () => {
		getForm()
			.validateFields()
			.then((data) => {
				setAddMaterialModelVisible(true);
			})
			.catch((ex) => {
				message.error("请输入主表单信息!");
			});
	};
	const onDelete = () => {
		if (!!selectedDocumentMaterialRowKeys) {
			removeDocumentMaterial(selectedDocumentMaterialRowKeys);
		}
	};

	const onDownloadTpl = () => {
		downloadDetailTpl({ showMsg: true, data: {} });
	};

	const onUploadSuccess = (retData) => {
		const mappedList = retData.map((el) => ({
			...el,
			key: uuidV4(),
			materialUnitName: getDictNameByCode(
				DICT_KEY.CM_DICT.MATERIAL_UNIT,
				el.materialUnit
			),
		}));
		setDocumentMaterialList([...documentMaterialList, ...mappedList]);
	};

	const handleSave = (row) => {
		setDocumentMaterialList(
			documentMaterialList.map((el) => {
				if (el.key === row.key) {
					let tmp = { ...el, ...row };
					const { returnNum, returnPrice, discount } = tmp;
					tmp.returnAmount = (returnNum * returnPrice).toFixed(2);
					tmp.receivableAmount = (
						(returnNum * returnPrice * discount) /
						100
					).toFixed(2);
					return tmp;
				} else {
					return el;
				}
			})
		);
	};

	const TableHeader = () => {
		return (
			<div style={{ margin: "0 0 8px", display: "flex" }}>
				<div className="detail-list-statistic color-primary">
					<span className="margin-left-8">
						退货总数量:
						{detailStatistics && detailStatistics.roTotalNum
							? detailStatistics.roTotalNum
							: 0}
					</span>
					<span className="margin-left-16">
						折后总计:
						{detailStatistics && detailStatistics.roReceivableAmount
							? detailStatistics.roReceivableAmount
							: 0}
					</span>
				</div>
				<div
					style={{ width: "460px", textAlign: "right" }}
					hidden={
						selectedDocument.orderStatus === "027004" || //禁用
						selectedDocument.orderStatus === "027003" //结算
					}
				>
					<YhAuth resCode={ResourceAuthEnum.PR_ED_ADD_M}>
						<Button
							type="primary"
							onClick={onAdd}
						>	新增商品  </Button>
					</YhAuth>
					<YhAuth resCode={ResourceAuthEnum.PR_ED_DEL_M}>
						<Button
							className="margin-left-8"
							onClick={onDelete}
							disabled={
								(selectedDocumentMaterialRowKeys &&
									selectedDocumentMaterialRowKeys.length === 0)
							}
						>删除商品  </Button>
					</YhAuth>
					<YhAuth resCode={ResourceAuthEnum.PR_ED_DLOAD}>
						<Button
							className="margin-left-8"
							onClick={onDownloadTpl}
						>下载模板</Button>
					</YhAuth>
					<YhAuth resCode={ResourceAuthEnum.PR_ED_IM}>
						<Upload  {...PurchaseRoService.getImportDetailCfg({ onUploadSuccess })}>
							<Button
								className="margin-left-8"
							>导入</Button>
						</Upload>
					</YhAuth>
				</div>
			</div>
		);
	};

	const columns = [
		{
			title: "序号",
			width: 50,
			render: (text, record, index) => `${index + 1}`,
		},

		{
			title: "商品编号",
			width: 120,
			dataIndex: "materialCode",
			align: "left",
		},
		{
			title: "商品名称",
			width: 180,
			ellipsis: true,
			dataIndex: "materialName",
		},
		/*{
				title: '图号',
				width: 120,
				dataIndex: 'replaceNum',
			},*/
		/*{
				title: '单位',
				width: 80,
				dataIndex: 'materialUnitName',
			},*/
		{
			title: "采购数量",
			width: 80,
			dataIndex: "purchaseNum",
		},

		{
			title: "赠品数量",
			width: 80,
			dataIndex: "giftsNum",
		},
		{
			title: "已退数量",
			width: 80,
			dataIndex: "retiredNum",
			render: (text) => (text ? text : 0),
		},
		{
			title: "退货数量*",
			width: 100,
			dataIndex: "returnNum",
			editable: editable02,
		},
		{
			title: "单价*",
			width: 140,
			dataIndex: "returnPrice",
			editable: editable01,
			render: (text) => formatNum(text),
		},
		{
			title: "总计",
			width: 140,
			dataIndex: "returnAmount",
			// render: text => formatNum(text),
			render: (text, record) => {
				const { returnNum, returnPrice } = record;
				return (returnNum * returnPrice).toFixed(2);
			},
		},
		{
			title: "折扣*",
			width: 100,
			dataIndex: "discount",
			editable: editable01,
			render: (text) => text + "%",
		},
		{
			title: "应收",
			width: 140,
			dataIndex: "receivableAmount",
			// render: text => formatNum(text),
			render: (text, record) => {
				const { returnNum, returnPrice, discount } = record;
				return ((returnNum * returnPrice * discount) / 100).toFixed(2);
			},
		},
		{
			title: "采购单号",
			width: 160,
			dataIndex: "purchaseCode",
			align: "left",
		},
		{
			title: "仓库",
			width: 160,
			ellipsis: true,
			dataIndex: "warehouseName",
		},
		{
			title: "货位",
			width: 120,
			dataIndex: "shelfCode",
		},
		{
			title: "备注*",
			width: 200,
			dataIndex: "returnDesc",
			editable: true,
			render: (text, record) => (record.returnDesc ? record.returnDesc : "空"),
		},
	];
	const components = {
		body: {
			row: EditableRow,
			cell: EditableCell,
		},
	};

	const mapColumns = (handleSave) => {
		return columns.map((col) => {
			if (!col.editable) {
				return col;
			}
			return {
				...col,
				onCell: (record) => ({
					record,
					editable: col.editable,
					dataIndex: col.dataIndex,
					title: col.title,
					prefix: col.prefix ?? "",
					suffix: col.suffix ?? "",
					selectList: col.selectList ?? [],
					handleSave,
					column: columns,
					editList: documentMaterialList,
				}),
			};
		});
	};
	return (
		<>
			<TableHeader />
			<Table
				components={components}
				columns={mapColumns(handleSave)}
				dataSource={documentMaterialList}
				bordered
				pagination={false}
				rowClassName={() => "editable-row"}
				rowSelection={{
					type: "checkbox",
					onChange: (selectedRowKeys) => {
						setSelectedDocumentMaterialRowKeys(selectedRowKeys);
					},
					selectedRowKeys: selectedDocumentMaterialRowKeys,
				}}
				scroll={{ x: "max-content", y: 420 }}
			/>

			<PurchaseItemSelectionModel />
		</>
	);
}
